import React, { useCallback, useMemo } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'components/Luxkit/TextLink'
import { SET_OPTIMIZELY_EXPERIMENT } from 'actions/actionConstants'
import SolidChartIcon from 'components/Luxkit/Icons/solid/SolidChartIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SwitchButton from 'components/Luxkit/Switch/SwitchButton'
import IconButton from 'components/Luxkit/Button/IconButton'
import Group from 'components/utils/Group'

interface ExperimentItemProps {
  experimentId: string;
  experiment: App.OptimizelyExperimentDetails;
}

/**
 * Formats the experiment title
 * @returns The title by replacing '_' with spaces and capitalising each word e.g. 'multi_item_cart' to 'Multi Item Cart'
 */
function formatExperimentTitle(experimentId: string): string {
  const experimentIdList = experimentId.split('_')
  if (experimentIdList.length === 0) {
    return ''
  }
  const division = experimentIdList[0].toUpperCase()
  const experimentNameList = experimentIdList.slice(1, experimentIdList.length)
  const experimentName = experimentNameList.map(word => word[0].toUpperCase() + word.slice(1, word.length)).join(' ')
  return `${division}: ${experimentName}`
}

export function ExperimentItem({ experimentId, experiment }: ExperimentItemProps) {
  const dispatch = useAppDispatch()

  const handleToggleEnabled = useCallback(() => {
    dispatch({
      type: SET_OPTIMIZELY_EXPERIMENT,
      flagKey: experimentId,
      value: {
        variationKey: !experiment.enabled ? 'on' : 'off',
        enabled: !experiment.enabled,
      },
    })
  }, [experimentId, experiment.enabled, dispatch])

  const experimentTitle = useMemo(() => formatExperimentTitle(experimentId), [experimentId])
  const reportAvailable = !(experiment.campaignId === undefined || experiment.experimentId === undefined)

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <TextLink
          underline={false}
          href={`https://app.optimizely.com/v2/projects/24799170005/flags/manage/${experimentId}`}
          target="_blank"
          weight="regular"
        >
          {experimentTitle}
        </TextLink>
        <BodyText variant="small" colour="neutral-three">
          {experimentId}
        </BodyText>
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <BodyText variant="medium" colour="neutral-two">
            Variant: <i>{experiment.variationKey ?? 'N/A'}</i>
          </BodyText>
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium" colour="neutral-two">
              Report:
            </BodyText>
            <IconButton
              kind="tertiary"
              size="small"
              href={`https://app.optimizely.com/v2/projects/24799170005/results/${experiment.campaignId}/experiments/${experiment.experimentId}`}
              target="_blank"
              disabled={!reportAvailable}
            >
              <SolidChartIcon />
            </IconButton>
          </Group>
        </Group>

        <SwitchButton
          checked={experiment.enabled}
          onChange={handleToggleEnabled}
        >
          {experiment.enabled ? 'On' : 'Off'}
        </SwitchButton>
      </Group>
    </Group>
  )
}

export default ExperimentItem
